import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, Phone } from "lucide-react";
import Logo from "./Logo";
import { COMPANY } from "../data/site";

const LINKS = [
    { to: "/", label: "Home", testid: "nav-home-link" },
    { to: "/solutions", label: "Solutions", testid: "nav-solutions-link" },
    { to: "/why-pacific", label: "Why Pacific", testid: "nav-whypacific-link" },
    { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 transition-shadow duration-300 ${
                scrolled || open ? "shadow-[0_4px_20px_rgba(0,0,0,0.08)]" : ""
            }`}
        >
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-[60] focus:bg-gold focus:text-ink focus:px-4 focus:py-2 focus:font-semibold"
            >
                Skip to content
            </a>
            <div className="container-px">
                <div className="flex h-20 sm:h-24 items-center justify-between gap-6">
                    <Logo />
                    <nav aria-label="Primary" className="hidden lg:block">
                        <ul className="flex items-center gap-9">
                            {LINKS.map((link) => (
                                <li key={link.to}>
                                    <NavLink
                                        to={link.to}
                                        data-testid={link.testid}
                                        className={({ isActive }) =>
                                            `relative font-heading text-[13px] font-semibold uppercase tracking-[0.18em] transition-colors duration-200 py-2 ${
                                                isActive
                                                    ? "text-gold"
                                                    : "text-ink/60 hover:text-ink"
                                            }`
                                        }
                                    >
                                        {link.label}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/contact"
                            data-testid="header-quote-button"
                            className="group hidden sm:inline-flex items-center gap-2 bg-gold px-6 py-3 font-heading text-[13px] font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-200 hover:bg-gold-light"
                        >
                            Request a Quote
                            <ArrowRight
                                size={15}
                                className="transition-transform duration-200 group-hover:translate-x-1"
                                aria-hidden="true"
                            />
                        </Link>
                        <button
                            type="button"
                            data-testid="mobile-menu-toggle"
                            onClick={() => setOpen((v) => !v)}
                            aria-expanded={open}
                            aria-controls="mobile-menu"
                            aria-label={open ? "Close menu" : "Open menu"}
                            className="lg:hidden inline-flex h-11 w-11 items-center justify-center text-ink border border-ink/20 hover:border-gold hover:text-gold transition-colors"
                        >
                            {open ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>
            </div>
            <div
                id="mobile-menu"
                className={`lg:hidden overflow-hidden transition-[max-height] duration-300 ease-out ${
                    open ? "max-h-[480px] border-t border-gray-200" : "max-h-0"
                } bg-white`}
            >
                <nav aria-label="Mobile" className="container-px py-6">
                    <ul className="flex flex-col gap-1">
                        {LINKS.map((link) => (
                            <li key={link.to}>
                                <NavLink
                                    to={link.to}
                                    data-testid={`mobile-${link.testid}`}
                                    className={({ isActive }) =>
                                        `block py-3 font-heading text-lg font-semibold uppercase tracking-[0.14em] border-b border-gray-100 ${
                                            isActive ? "text-gold" : "text-ink/80"
                                        }`
                                    }
                                >
                                    {link.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                    <Link
                        to="/contact"
                        data-testid="mobile-quote-button"
                        className="mt-5 flex items-center justify-center gap-2 bg-gold px-6 py-4 font-heading text-sm font-bold uppercase tracking-[0.14em] text-ink"
                    >
                        Request a Quote <ArrowRight size={16} aria-hidden="true" />
                    </Link>
                    <a
                        href={COMPANY.phoneHref}
                        data-testid="mobile-call-link"
                        className="mt-3 flex items-center justify-center gap-2 border border-ink/20 px-6 py-4 font-heading text-sm font-semibold uppercase tracking-[0.14em] text-ink"
                    >
                        <Phone size={16} aria-hidden="true" /> {COMPANY.phone}
                    </a>
                </nav>
            </div>
        </header>
    );
}
